import { useDatabase } from "@nozbe/watermelondb/hooks";
import {
  ProductKitItem,
  ProductLocationInventoryPricing,
  Sale,
  SaleAdjustmentType,
} from "@towersystems/roam-common/lib/generated-types";
import { useCallback, useContext, useState } from "react";
import {
  useFlashMessages,
  useWithAppState,
} from "@towersystems/roam-common-native";
import Product from "../../../../utilities/data/model/Product";
import SaleModel, {
  AddItemInput,
  SaleState,
} from "../../../../utilities/data/model/Sale";
import { useSaleSyncProvider } from "../../../../utilities/sale-sync";
import { hydrateData, usePersistSale } from "../../../sales/utils";
import { resolveSaleLocationPricing } from "../sale-price-resolver";
import { SaleControllerContext } from "./context";
import { SyncState } from "../../../../utilities/data/constants";
import { useMutation } from "@apollo/client";
import { createSale, updateSale } from "../sale/constants";
import { compareWithTolerance } from "../../../../utilities/compare-with-tolerance";

export const useSaleControllerProvider = () => {
  const context = useContext(SaleControllerContext);
  if (!context) {
    throw new Error("No SaleControllerContext context was provided. ");
  }
  return context;
};

export type AddItemOptions = {
  serialNumber?: string;
  skipVariantPicker?: boolean;
  skipPriceCheck?: boolean;
  basePrice?: number;
  giftCardNumber?: string;
  stockNumber?: string;
  name?: string;
  quantity?: number;
  taxRate?: number;
  // costPrice?: number;
  metadata?: any;
};

export type UseAddItemOptions = {
  navigation: any;
};

export const useAddCustomerToSale = ({ navigation }: { navigation: any }) => {
  const database = useDatabase();
  const saleController = useSaleControllerProvider();
  const { showMessage } = useFlashMessages();
  const parkSale = useParkSale();

  /**
   * Adds a customer to the sale and returns the updated sale
   */
  const addCustomerAndMakeSaleByCustomerId = async (customerId: string) => {
    const customer = await database.collections
      .get("customers")
      .find(customerId);

    if (!customer) {
      throw new Error(`error.customer-not-found-${customerId}`);
    }

    const sale =
      saleController.sale ?? (await saleController.createEmptySale());

    await sale.addCustomer(customerId);
    return sale;
  };

  const parkSaleAndAddCustomer = async (customerId: string) => {
    if (saleController.sale) {
      await parkSale("Customer scanned. Sale parked.");
    }

    const sale = await saleController.createEmptySale();
    await sale.addCustomer(customerId);
    return sale;
  };

  return {
    parkSaleAndAddCustomer,
    addCustomerAndMakeSaleByCustomerId,
  };
};

type ComponentConfig = {
  productId: string;
  quantity: number;
  price: string;
};

export const useAddItem = ({ navigation }: UseAddItemOptions) => {
  const database = useDatabase();
  const saleController = useSaleControllerProvider();
  const { showMessage } = useFlashMessages();

  const addItem = async (productId: string, options?: AddItemOptions) => {
    const sale =
      saleController.sale ?? (await saleController.createEmptySale());

    const product = await database.collections
      .get<Product>("products")
      .find(productId);

    if (!product || product.active === false) {
      throw new Error(`error.product-not-found-${productId}`);
    }

    if (product.hasVariants && !options?.skipVariantPicker) {
      return navigation.navigate("VariantPickerModalScreen", {
        productId,
      });
    }

    if (product.serialNumbersSale && !options?.serialNumber) {
      return navigation.navigate("SerialNumberRootScreen", {
        screen: "ListProductSerialNumbersScreen",
        params: { productId },
      });
    }

    const { basePrice, skipPriceCheck, taxRate, ...remainingOptions } =
      options || {};

    const pricing: ProductLocationInventoryPricing | undefined =
      resolveSaleLocationPricing(sale, product);

    let resolvedBasePrice = basePrice ? basePrice : pricing?.retailPrice || 0;

    const maybeCatalogPrice = pricing?.cataloguePrice || 0;

    const taxRatePercent =
      taxRate !== undefined ? taxRate : pricing?.taxRatePercent || 0;
    console.log("taxRatePercent", taxRate, taxRatePercent);

    if (product.isGiftCard && !options?.giftCardNumber) {
      navigation.navigate("AddGiftCardProductCartRootScreen", {
        screen: "SearchOrAddGiftCardScreen",
        productId: productId,
      });
      return;
    }

    if (
      false === options?.skipPriceCheck &&
      (!resolvedBasePrice || resolvedBasePrice === 0)
    ) {
      navigation.navigate("AddNonPricedItemModalScreen", {
        productId,
      });
      return;
    }

    if (product.isKit) {
      const productKit = await product.productKit.fetch();
      resolvedBasePrice = productKit.price;

      const kitItems = await productKit.items.fetch();

      for (const item of kitItems) {
        const itemProduct = await item.product.fetch();

        const hasComponent = remainingOptions?.metadata?.components?.[item.id];

        if (itemProduct.hasVariants && !hasComponent) {
          return navigation.navigate("KitResolverModalScreen", {
            productId: product.id,
          });
        }

        // set the component metadata if not already set
        if (!hasComponent) {
          remainingOptions.metadata = {
            ...remainingOptions?.metadata,
            components: {
              ...remainingOptions?.metadata?.components,
              [item.id]: {
                productId: itemProduct.id,
                quantity: item.quantity,
                price: item.price || 0,
              },
            },
          };
        }
      }
    }

    const catalogueDiscount =
      maybeCatalogPrice > 0 && maybeCatalogPrice < resolvedBasePrice
        ? resolvedBasePrice - maybeCatalogPrice
        : 0;

    try {
      const item = await sale.addItem({
        productId,
        quantity: 1,
        basePrice: resolvedBasePrice,
        discount: 0,
        taxRatePercent: taxRatePercent,
        giftCardNumber: options?.giftCardNumber,
        ...remainingOptions,
      });

      if (catalogueDiscount > 0) {
        await item.addAdjustment({
          type: SaleAdjustmentType.CATALOGUE_DISCOUNT,
          amount: catalogueDiscount,
        });
      }
    } catch (err) {
      if (err instanceof Error) {
        showMessage({
          message: err.message,
          severity: "error",
        });
      }
      throw err;
    }
  };

  return addItem;
};

export const useCloseSale = () => {
  // check if the sale can be closed
  const saleController = useSaleControllerProvider();
  const { updateAppState } = useWithAppState();
  const { addSaleToQueue } = useSaleSyncProvider();

  const closeSale = useCallback(async () => {
    const sale = saleController.sale;

    if (!sale) {
      throw new Error("error.no-sale-to-close");
    }

    // attempt to close the sale
    await sale.closeSale();

    // clear the local sale id from the app state
    updateAppState({ localSaleId: undefined });

    // update the sync state to upload so that the sale will be uploaded to the server
    await sale.updateSyncState(SyncState.UPLOAD);

    await addSaleToQueue(sale.id);
  }, [saleController.sale]);

  return closeSale;
};

export const useClearCart = () => {
  const saleController = useSaleControllerProvider();
  const sale = saleController.sale;

  const clearCart = async () => {
    if (!sale) {
      throw new Error("error.no-sale");
    }
    await sale.clearItems();
  };

  return clearCart;
};

export const useParkSale = () => {
  const saleController = useSaleControllerProvider();
  const { updateAppState } = useWithAppState();
  const { addSaleToQueue } = useSaleSyncProvider();

  const parkSale = useCallback(
    async (note?: string, cb?: () => Promise<undefined>) => {
      const sale = saleController.sale;
      if (!sale) {
        throw new Error("error.no-sale-to-park");
      }

      let toState = sale.saveableState;

      // update the sale state to parked
      await sale.transitionToState(toState);

      // clear the local sale id from the app state
      updateAppState({ localSaleId: undefined });

      if (note !== undefined) {
        await sale.updateSaleNote(note);
      }

      // update the sync state to upload so that the sale will be uploaded to the server
      await sale.updateSyncState(SyncState.UPLOAD);
      await addSaleToQueue(sale.id);

      if (cb) {
        await cb();
      }
    },
    [saleController.sale],
  );

  return parkSale;
};

export const usePersistActiveSale = () => {
  const saleController = useSaleControllerProvider();
  const persistSale = usePersistSale();
  const saleSync = useSaleSyncProvider();
  const [updateSaleMutation] = useMutation<{ updateSale: Sale }>(updateSale);
  const [createSaleMutation] = useMutation<{ createSale: Sale }>(createSale);
  const { updateAppState } = useWithAppState();

  const persistActiveSale = async (input?: Sale) => {
    const sale = saleController.sale;

    if (!sale) {
      throw new Error("error.no-sale-to-persist");
    }

    let toState = sale.saveableState;
    await sale.transitionToState(toState);

    const saleInput = input ?? (await saleSync.formatSale(sale));

    let saleResult: Sale | undefined;

    //@ts-ignore
    if (saleInput.id) {
      const result = await updateSaleMutation({
        variables: { input: saleInput },
      });
      saleResult = result.data?.updateSale;
    } else {
      const result = await createSaleMutation({
        variables: { input: saleInput },
      });
      saleResult = result.data?.createSale;
    }

    if (saleResult) {
      const updatedSale = await persistSale(saleResult);

      updateAppState({ localSaleId: updatedSale._raw.id });
      return updatedSale;
    }

    throw new Error("error.sale-not-found");
  };

  return persistActiveSale;
};

export const useParkSaleSyncronously = () => {
  const saleController = useSaleControllerProvider();
  const { formatSale } = useSaleSyncProvider();
  const persistSale = usePersistSale();
  const [updateSaleMutation] = useMutation<{ updateSale: Sale }>(updateSale);
  const [createSaleMutation] = useMutation<{ createSale: Sale }>(createSale);

  const { updateAppState } = useWithAppState();

  const parkSale = useCallback(
    async (note?: string, clearLocalSale: boolean = true) => {
      const sale = saleController.sale;

      if (!sale) {
        throw new Error("error.no-sale-to-park");
      }

      let toState = sale.saveableState;

      // update the sale state to parked
      await sale.transitionToState(toState);
      await sale.updateSaleNote(note);

      const formattedSale = await formatSale(sale);
      let maybeSale: Sale | undefined = undefined;

      //@ts-expect-error
      if (formattedSale.id) {
        const result = await updateSaleMutation({
          variables: { input: formattedSale },
        });
        maybeSale = result.data?.updateSale;
      } else {
        const result = await createSaleMutation({
          variables: { input: formattedSale },
        });
        maybeSale = result.data?.createSale;
      }

      if (!maybeSale) {
        throw new Error("error.sale-not-found");
      }

      const newSale = await persistSale(maybeSale);

      if (clearLocalSale) {
        console.log("clearing local sale");
        // clear the local sale id from the app state
        updateAppState({ localSaleId: undefined });
      }

      if (false == clearLocalSale) {
        console.log("updating local sale");
        updateAppState({ localSaleId: newSale.id });
      }

      // user will need to retreive the parked sale from server
      /*
      database.write(async () => {
        const items = await (sale?.items.fetch() || []);
        for (const item of items) {
          await item.destroyPermanently();
        }

        await sale.destroyPermanently();
      });
      */

      return maybeSale;
    },
    [saleController.sale],
  );

  return parkSale;
};

export const useCancelSale = () => {
  const saleController = useSaleControllerProvider();
  const { updateAppState } = useWithAppState();
  const { addSaleToQueue } = useSaleSyncProvider();
  const cancelSale = useCallback(
    async (note?: string) => {
      const sale = saleController.sale;
      if (!sale) {
        throw new Error("error.no-sale-to-cancel");
      }

      const paymentsTotal = sale.settledPayments.reduce(
        (total, payment) => total + payment.amount,
        0,
      );

      if (false === compareWithTolerance(paymentsTotal, 0)) {
        throw new Error(
          "error.sale-cannot-be-cancelled-when-payments-total-is-not-zero",
        );
      }

      // update the sale state to parked
      await sale.cancelSale();

      if (note !== undefined) {
        await sale.updateSaleNote(note);
      }

      // clear the local sale id from the app state
      updateAppState({ localSaleId: undefined });
      // update the sync state to upload so that the sale will be uploaded to the server
      await sale.updateSyncState(SyncState.UPLOAD);
      await addSaleToQueue(sale.id);
    },
    [saleController.sale],
  );

  return cancelSale;
};

export const useSwitchSale = () => {
  const database = useDatabase();
  const parkSale = useParkSale();
  const persistSale = usePersistSale();
  const { updateAppState, appState } = useWithAppState();

  const switchSale = async (sale: Sale | SaleModel) => {
    if (sale.id == appState.localSaleId) {
      throw new Error("error.already-active-sale");
    }

    if (sale instanceof SaleModel) {
      try {
        await parkSale();
        updateAppState({ localSaleId: sale.id });
        await sale.updateSyncState(SyncState.MODIFED);
        return undefined;
      } catch (e: any) {
        if (e.message == "error.no-sale-to-park") {
        }
        console.log(e);
      }

      return;
    }

    if (
      ![
        SaleState.SAVED,
        SaleState.OPEN,
        SaleState.INVOICE_SAVED,
        SaleState.INVOICE_OPEN,
        SaleState.LAYBY_SAVED,
        SaleState.LAYBY_OPEN,
        SaleState.QUOTE_SAVED,
        SaleState.QUOTE_OPEN,
        SaleState.PROFORMA_SAVED,
      ].includes(sale.state as SaleState)
    ) {
      throw new Error("error.invalid-state");
    }

    if (!appState.locationId || appState.locationId != sale.locationId) {
      throw new Error("error.invalid-location");
    }

    let existingSale: SaleModel | undefined;

    try {
      existingSale = await database.collections
        .get<SaleModel>("sales")
        .find(sale.id);
    } catch (error) {}

    if (existingSale && existingSale.syncState === SyncState.UPLOAD) {
      throw new Error("error.sale-in-queue");
    }

    try {
      await parkSale();
    } catch (e: any) {
      if (e.message == "error.no-sale-to-park") {
      }
      console.log(e);
    }

    const newSale = await persistSale(sale);
    updateAppState({ localSaleId: newSale.id });
    await newSale.updateSyncState(SyncState.MODIFED);
    return undefined;
  };

  return switchSale;
};
